import React, { useEffect, useState } from "react";
import { Grid, Button } from "@mui/material";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, PieChart, Pie, Label, Cell, Area, AreaChart } from "recharts";
import { Banknote, Package, Trophy, UsersRound } from "lucide-react";
import { getProductPerformanceReportAction } from "../../Actions/ReportsAction";
import CustomSnackbar from "../Common/snackBar";
import FilterDrawer from "../../components/DataTable/FilterDrawer";
import { TableFilte } from "../../Assets/svg/svgicon";
import ReportKpiCards from "./ReportKpiCards";
import {
    AreaGradient,
    ChartLegend,
    ChartPanel,
    formatCompact,
    PORTAL_COLORS,
    portalColor,
    rechartsAxis,
    rechartsGrid,
    ReportTooltip,
    RingCenter,
} from "./reportCharts";

const drawerFilters = [
    {
        name: "dateRange",
        label: "Date Range",
        type: "dateRange",
        placeholder: "Select date range",
    },
];

const ProductPerformanceReport = () => {
    const [loading, setLoading] = useState(false);

    const getDefaultDateRange = () => {
        const to = new Date();
        const from = new Date();
        from.setMonth(from.getMonth() - 6);
        return { from, to };
    };

    const [filterValues, setFilterValues] = useState({
        dateRange: getDefaultDateRange(),
    });

    const [filterAnchorEl, setFilterAnchorEl] = useState(null);

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success",
    });

    const [summary, setSummary] = useState({
        totalRevenue: 0,
        totalProducts: 0,
        topRevenueProduct: "-",
        fastestGrowingProduct: "-",
        mostCustomersProduct: "-",
    });

    const [products, setProducts] = useState([]);

    const buildApiFilters = (fv) => ({
        startDate: fv.dateRange?.from ? fv.dateRange.from.toISOString().split("T")[0] : "",
        endDate: fv.dateRange?.to ? fv.dateRange.to.toISOString().split("T")[0] : "",
    });

    const fetchReport = async (fv = filterValues) => {
        try {
            setLoading(true);
            const response = await getProductPerformanceReportAction({
                data: buildApiFilters(fv),
            });
            setSummary(response?.data?.summary || {});
            setProducts(response?.data?.products || []);
        } catch (error) {
            setSnackbar({
                open: true,
                message: error?.response?.data?.message || "Something went wrong",
                severity: "error",
            });
        } finally {
            setLoading(false);
        }
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const handleFilterApply = (newValues) => {
        setFilterValues(newValues);
        fetchReport(newValues);
    };

    const handleFilterReset = () => {
        const resetValues = { dateRange: getDefaultDateRange() };
        setFilterValues(resetValues);
        fetchReport(resetValues);
    };

    useEffect(() => {
        fetchReport();
    }, []);

    const hasAppliedFilters = false; // date range always pre-set, no extra indicator needed

    const revenueChartData = products.map((item) => ({
        name: item.product,
        revenue: item.revenue,
    }));

    const tenantChartData = products.map((item) => ({
        name: item.product,
        value: item.tenants,
    }));

    const tenantLegend = tenantChartData.map((item) => ({
        label: item.name,
        value: Number(item.value || 0),
        color: portalColor(item.name),
    }));

    const tenantTotal = tenantLegend.reduce((sum, item) => sum + item.value, 0);

    return (
        <>
            <Grid container spacing={3}>
                {/* Filters */}
                <Grid item xs={12}>
                    <div className="card border-rounded-0 fiter-card">
                        <div className="card-body p-5 pb-0">
                            <div className="row align-items-center mb-5">
                                <div className="col-auto">
                                    <h2 className="chart-title mb-0">
                                        Product Performance Report
                                    </h2>
                                </div>

                                <div className="col-auto ms-auto d-flex gap-2">
                                    <Button
                                        variant="outlined"
                                        onClick={(e) => setFilterAnchorEl(e.currentTarget)}
                                        startIcon={<TableFilte />}
                                        className={`dt-filter-btn ${hasAppliedFilters ? "active" : ""}`}
                                    >
                                        Filters
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                </Grid>

                {/* KPI Cards */}
                <Grid item xs={12}>
                    <ReportKpiCards
                        loading={loading}
                        cards={[
                            {
                                key: "revenue",
                                label: "Total Revenue",
                                value: Number(summary.totalRevenue || 0),
                                accent: "#22419e",
                                icon: <Banknote />,
                            },
                            {
                                key: "products",
                                label: "Total Products",
                                value: summary.totalProducts || 0,
                                accent: "#0891b2",
                                icon: <Package />,
                            },
                            {
                                key: "topRevenue",
                                label: "Top Revenue Product",
                                value: summary.topRevenueProduct || "—",
                                accent: "#7c3aed",
                                icon: <Trophy />,
                                compact: true,
                            },
                            {
                                key: "mostCustomers",
                                label: "Most Customers",
                                value: summary.mostCustomersProduct || "—",
                                accent: "#059669",
                                icon: <UsersRound />,
                                compact: true,
                            },
                        ]}
                    />
                </Grid>

                {/* Charts */}
                <Grid container spacing={3} mt={1} sx={{ width: "100%", marginLeft: 0 }}>
                    <Grid item xs={12} md={8}>
                        <ChartPanel
                            title="Revenue by product"
                            caption="Revenue earned per product"
                            height={330}
                            loading={loading}
                            isEmpty={revenueChartData.length === 0}
                        >
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={revenueChartData} barCategoryGap="34%">
                                    <CartesianGrid {...rechartsGrid} />
                                    <XAxis dataKey="name" {...rechartsAxis} />
                                    <YAxis
                                        {...rechartsAxis}
                                        width={48}
                                        tickFormatter={formatCompact}
                                    />
                                    <Tooltip
                                        content={<ReportTooltip />}
                                        cursor={{ fill: "rgba(34, 65, 158, 0.05)" }}
                                    />
                                    <Bar dataKey="revenue" radius={[8, 8, 0, 0]} maxBarSize={44}>
                                        {revenueChartData.map((entry) => (
                                            <Cell key={entry.name} fill={portalColor(entry.name)} />
                                        ))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </ChartPanel>
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <ChartPanel
                            title="Tenant distribution"
                            caption="Tenants active on each product"
                            height={330}
                            loading={loading}
                            isEmpty={tenantTotal === 0}
                        >
                            <div className="report-ring">
                                <div className="report-ring-chart">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <PieChart>
                                            <Pie
                                                data={tenantChartData}
                                                dataKey="value"
                                                nameKey="name"
                                                innerRadius="64%"
                                                outerRadius="92%"
                                                paddingAngle={2}
                                                cornerRadius={6}
                                                stroke="#ffffff"
                                                strokeWidth={2}
                                            >
                                                {tenantChartData.map((entry) => (
                                                    <Cell
                                                        key={entry.name}
                                                        fill={portalColor(entry.name)}
                                                    />
                                                ))}
                                                <Label
                                                    position="center"
                                                    content={
                                                        <RingCenter
                                                            caption="Tenants"
                                                            value={formatCompact(tenantTotal)}
                                                        />
                                                    }
                                                />
                                            </Pie>
                                            <Tooltip content={<ReportTooltip />} />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </div>
                                <ChartLegend items={tenantLegend} />
                            </div>
                        </ChartPanel>
                    </Grid>

                    <Grid item xs={12}>
                        <ChartPanel
                            title="Revenue distribution"
                            caption="Revenue spread across the product mix"
                            height={300}
                            loading={loading}
                            isEmpty={revenueChartData.length === 0}
                        >
                            <ResponsiveContainer>
                                <AreaChart data={revenueChartData}>
                                    <AreaGradient
                                        id="productRevenueFill"
                                        color={PORTAL_COLORS.qbot}
                                    />
                                    <CartesianGrid {...rechartsGrid} />
                                    <XAxis dataKey="name" {...rechartsAxis} />
                                    <YAxis
                                        {...rechartsAxis}
                                        width={48}
                                        tickFormatter={formatCompact}
                                    />
                                    <Tooltip content={<ReportTooltip />} />
                                    <Area
                                        type="monotone"
                                        dataKey="revenue"
                                        stroke={PORTAL_COLORS.qbot}
                                        strokeWidth={2}
                                        fill="url(#productRevenueFill)"
                                        activeDot={{
                                            r: 4,
                                            fill: "#ffffff",
                                            stroke: PORTAL_COLORS.qbot,
                                            strokeWidth: 2,
                                        }}
                                    />
                                </AreaChart>
                            </ResponsiveContainer>
                        </ChartPanel>
                    </Grid>
                </Grid>
            </Grid>

            {/* Filter Drawer */}
            <FilterDrawer
                open={Boolean(filterAnchorEl)}
                anchorEl={filterAnchorEl}
                onClose={() => setFilterAnchorEl(null)}
                filters={drawerFilters}
                filterValues={filterValues}
                onApply={handleFilterApply}
                onReset={handleFilterReset}
            />

            <CustomSnackbar
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                onClose={handleCloseSnackbar}
            />
        </>
    );
};

export default ProductPerformanceReport;
